import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useProfile, profileQueryOptions } from "@/lib/useProfile";
import {
  Home,
  Gem,
  Sparkles,
  User,
  LogOut,
} from "lucide-react";
import { useShareCaileve } from "@/hooks/use-share-caileve";
import { FreezeBanner } from "@/components/freeze-banner";
import { useSessionGuard } from "@/lib/session-guard";
import { Sentry } from "@/lib/sentry";

import {
  consumeStaleNativeHandoff,
  isNativeHandoffActive,
  subscribeNativeHandoff,
} from "@/lib/native-handoff";

export const pullSignal = {
  distance: 0,
  refreshing: false,
  listeners: new Set<() => void>(),
  notify() { this.listeners.forEach((fn) => fn()); },
};

const BOTTOM_NAV: { to: string; label: string; Icon: typeof Home; exact?: boolean; matchPaths?: string[]; isShare?: boolean }[] = [
  { to: "/", label: "Home", Icon: Home, exact: true },
  { to: "/jewelry", label: "Closet", Icon: Gem, matchPaths: ["/jewelry", "/clothing", "/accessories"] },
  { to: "/looks", label: "Lookbook", Icon: Sparkles },
  { to: "/account", label: "Profile", Icon: User },
];

const LILAC = "var(--caileve-lilac)";
const INK = "var(--caileve-ink)";
const MUTED = "rgba(30, 22, 36, 0.55)";
const NAV_BG = "var(--caileve-soft)";
const BG = "var(--caileve-bg)";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;


const SCREEN_TITLES: Record<string, string> = {
  "/jewelry": "Closet",
  "/clothing": "Closet",
  "/accessories": "Closet",
  "/looks": "Lookbook",
  "/suggest": "Style Me",
  "/requests": "Previous Styles",
  "/account": "Your Cailève",
  "/plans": "Plans",
  "/faq": "Help",
  "/about": "About",
  "/terms": "Terms",
  "/privacy": "Privacy",
};

export function AppShell({ children }: { children: ReactNode }) {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHome = pathname === "/";
  const isRequestDetailRoute = pathname.startsWith("/requests/") && UUID_PATTERN.test(pathname.split("/")[2] ?? "");
  const screenTitle = Object.entries(SCREEN_TITLES)
    .sort((a, b) => b[0].length - a[0].length)
    .find(([path]) => pathname === path || pathname.startsWith(path + "/"))?.[1] ?? null;
  const share = useShareCaileve();

  useSessionGuard();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const touchStartYRef = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const pullDistanceRef = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  const PULL_THRESHOLD = 72;
  // Tracks the last time the tab regained visibility/focus. We use this to
  // suppress the onboarding auto-redirect for a short grace window after a
  // native handoff (camera / photo library), where the React Query
  // onboarding_check may briefly refetch with stale/incomplete state.
  const lastResumeAtRef = useRef(0);
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Clear any stale handoff flag left over from a force-close mid-handoff.
    consumeStaleNativeHandoff();
    const onResume = () => {
      lastResumeAtRef.current = Date.now();
      forceTick((n) => n + 1);
    };
    const onVis = () => {
      if (document.visibilityState === "visible") onResume();
    };
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onVis);
    const unsub = subscribeNativeHandoff(() => forceTick((n) => n + 1));
    return () => {
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onVis);
      unsub();
    };
  }, []);
  const initialLoadCompleteRef = useRef(false);
  const [profileReady, setProfileReady] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    if (!isStandalone) return;
    const timeout = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timeout);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const iw = window.innerWidth;
      const cw = document.documentElement.clientWidth;
      if (Math.abs(iw - cw) > 2) {
        document.body.classList.add("__viewport_reflow");
        // force reflow
        void document.body.offsetWidth;
        document.body.classList.remove("__viewport_reflow");
      }
    } catch {
      // ignore
    }
  }, []);
  useEffect(() => {
    if (typeof document === "undefined" || !("fonts" in document)) {
      setFontsReady(true);
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setFontsReady(true);
    };
    const timeout = setTimeout(finish, 2000);
    Promise.all([
      (document as Document).fonts.load("500 1rem 'Cormorant Garamond'"),
      (document as Document).fonts.load("italic 400 1rem 'Cormorant Garamond'"),
    ])
      .then(() => (document as Document).fonts.ready)
      .then(() => {
        clearTimeout(timeout);
        finish();
      })
      .catch(() => {
        clearTimeout(timeout);
        finish();
      });
    return () => clearTimeout(timeout);
  }, []);
  const uidForProfile = user?.id ?? null;
  const { isFetched: profileFetched, isError: profileError } = useProfile();
  useEffect(() => {
    if (loading) return;
    if (!uidForProfile) {
      setProfileReady(true);
      return;
    }
    if (profileFetched || profileError) {
      setProfileReady(true);
      return;
    }
    const timeout = setTimeout(() => {
      Sentry.captureMessage("Profile fetch timeout on initial load", "warning");
      setProfileReady(true);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [loading, uidForProfile, profileFetched, profileError]);
  const fullyReady = !loading && profileReady && fontsReady;
  const showSplash = !fullyReady && !initialLoadCompleteRef.current;
  if (fullyReady) initialLoadCompleteRef.current = true;

  useEffect(() => {
    try {
      localStorage.removeItem("caileve:look-builder:v1");
      localStorage.removeItem("caileve_builder_ever_used");
      localStorage.removeItem("caileve:look-builder:last-detail-path");
    } catch {}
  }, []);

  useEffect(() => {
    if (loading && isRequestDetailRoute) return;
    if (!loading && !user) navigate({ to: "/login" });
  }, [user, loading, navigate, isRequestDetailRoute, pathname]);

  useEffect(() => {
    if (!user) return;
    const processQueue = async () => {
      const { data: pending } = await supabase
        .from("email_log")
        .select("id, template")
        .eq("user_id", user.id)
        .is("sent_at", null);
      if (!pending || pending.length === 0) return;
      for (const row of pending) {
        const { data: claimed } = await supabase
          .from("email_log")
          .update({ sent_at: new Date().toISOString() })
          .eq("id", row.id)
          .is("sent_at", null)
          .select()
          .maybeSingle();
        if (!claimed) continue; // another tab already claimed this row
        try {
          await supabase.functions.invoke("send-email", {
            body: { to: user.email, template: row.template },
          });
        } catch (err) {
          console.error(`[email-queue] Failed to send ${row.template}:`, err);
          // Row stays marked sent_at to avoid retry storms on a
          // persistent failure — do not revert the claim.
        }
      }
    };
    processQueue();
  }, [user?.id]);

  const uid = user?.id;
  const { data: onboardCheck } = useQuery({
    queryKey: ["onboarding_check", uid],
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [{ count: jCount }, { count: cCount }, { count: lCount }] =
        await Promise.all([
          supabase
            .from("jewelry_items")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid!),
          supabase
            .from("clothing_items")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid!),
          supabase
            .from("saved_looks")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid!),
        ]);
      // onboarding_complete is already in the profile row fetched by useProfile().
      // Read it from the React Query cache rather than issuing a redundant profiles request.
      const cachedProfile = qc.getQueryData<{ onboarding_complete?: boolean } | null>(
        profileQueryOptions(uid).queryKey,
      );
      const done = !!cachedProfile?.onboarding_complete;
      const empty = (jCount ?? 0) === 0 && (cCount ?? 0) === 0 && (lCount ?? 0) === 0;
      return { done, empty };
    },
  });

  useEffect(() => {
    if (!onboardCheck) return;
    // Suppress redirects while a native picker is in flight, and for a brief
    // grace window after the tab regains focus — this prevents getting
    // bounced to /onboarding when returning from camera or photo library.
    if (isNativeHandoffActive()) return;
    if (lastResumeAtRef.current && Date.now() - lastResumeAtRef.current < 1500) return;
    if (!onboardCheck.done && onboardCheck.empty && pathname !== "/onboarding") {
      navigate({ to: "/onboarding" });
    }
  }, [onboardCheck, pathname, navigate]);

  // Safety auto-heal: if the user has any collection data but onboarding_complete
  // is still false, mark it complete so route guards never trap them on onboarding.
  useEffect(() => {
    if (!uid || !onboardCheck) return;
    if (!onboardCheck.done && !onboardCheck.empty) {
      supabase
        .from("profiles")
        .update({ onboarding_complete: true } as never)
        .eq("id", uid)
        .then(() => {
          // no-op
        });
    }
  }, [uid, onboardCheck]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;
    if (scrollEl.scrollTop > 2) return;
    if (document.querySelector('[data-state="open"]')) return;
    touchStartYRef.current = e.touches[0]?.clientY ?? 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl || refreshing) return;
    if (scrollEl.scrollTop > 2) return;
    if (document.querySelector('[data-state="open"]')) return;
    const delta = (e.touches[0]?.clientY ?? 0) - touchStartYRef.current;
    if (delta <= 0) {
      pullDistanceRef.current = 0;
      return;
    }
    const clamped = Math.min(delta * 0.45, PULL_THRESHOLD + 20);
    pullDistanceRef.current = clamped;
    setPullDistance(clamped);
    pullSignal.distance = clamped;
    pullSignal.notify();
  };

  const handleTouchEnd = async () => {
    const current = pullDistanceRef.current;
    pullDistanceRef.current = 0;
    setPullDistance(0);
    pullSignal.distance = 0;
    pullSignal.refreshing = current >= PULL_THRESHOLD;
    pullSignal.notify();
    if (current >= PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      try {
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.update();
            const newWorker = registration.installing || registration.waiting;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "activated") {
                  window.location.reload();
                }
              });
              newWorker.postMessage({ type: "SKIP_WAITING" });
              return;
            }
          }
        }
        await qc.invalidateQueries();
        await qc.refetchQueries({ type: "active" });
      } finally {
        setTimeout(() => setRefreshing(false), 800);
        pullSignal.refreshing = false;
        pullSignal.distance = 0;
        pullSignal.notify();
      }
    }
  };

  if (showSplash) {
    return (
      <div
        className="fixed inset-0"
        style={{ backgroundColor: BG, width: "100vw", height: "100vh" }}
      />
    );
  }

  if (!user) return null;

  return (
    <div
      ref={scrollContainerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="bg-background"
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: `calc(100px + max(env(safe-area-inset-bottom), 8px))`,
      }}
    >
      <header
        className={`${
          isHome ? "border-transparent" : "border-border"
        } border-b bg-background/80 backdrop-blur sticky top-0 z-30`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {isHome || !screenTitle ? (
            <Link
              to="/"
              className="font-serif-display text-xl tracking-[0.18em] md:text-[22px] md:tracking-[4px] uppercase"
              style={{ color: INK }}
            >
              Cailève
            </Link>
          ) : (
            <p
              className="font-serif-display text-xl tracking-[0.18em] uppercase"
              style={{ color: INK }}
            >
              {screenTitle}
            </p>
          )}
          <div className="flex items-center gap-3">
          </div>
        </div>
      </header>
      <main>
        {children}
      </main>

      <FreezeBanner />
    </div>
  );
}

export function PullIndicator() {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const THRESHOLD = 72;
  useEffect(() => {
    const update = () => {
      setDistance(pullSignal.distance);
      setRefreshing(pullSignal.refreshing);
    };
    pullSignal.listeners.add(update);
    return () => { pullSignal.listeners.delete(update); };
  }, []);
  const h = refreshing ? 52 : Math.min(distance, 52);
  if (h <= 2 && !refreshing) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: h,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        pointerEvents: "none",
        transition: refreshing ? "none" : "height 80ms linear",
      }}
    >
      {refreshing ? (
        <div
          style={{
            width: 18,
            height: 18,
            border: `2px solid ${LILAC}`,
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
      ) : distance >= THRESHOLD ? (
        <span
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: 13,
            color: LILAC,
            letterSpacing: "0.08em",
          }}
        >
          Release to refresh
        </span>
      ) : (
        <span
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: 13,
            color: MUTED,
            letterSpacing: "0.08em",
            opacity: distance / THRESHOLD,
          }}
        >
          Pull to refresh
        </span>
      )}
    </div>
  );
}

export function PersistentNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const share = useShareCaileve();
  const { user } = useAuth();
  if (!user) return null;
  return (
    <nav
      className=""
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom), 8px)",
        paddingTop: 10,
        backgroundColor: NAV_BG,
        borderTop: "1px solid rgba(155, 127, 168, 0.25)",
        minHeight: 88,
        boxSizing: "border-box" as const,
        isolation: "isolate",
        willChange: "auto",
        transform: "translateZ(0)",
      }}
    >
      <ul className="max-w-2xl mx-auto grid grid-cols-4">
        {BOTTOM_NAV.map(({ to, label, Icon, exact, matchPaths, isShare }) => {
          const active = exact
            ? pathname === to
            : matchPaths
              ? matchPaths.some((p) => pathname === p || pathname.startsWith(p + "/"))
              : pathname === to || pathname.startsWith(to + "/");
          return (
            <li key={`${to}-${label}`} className="flex items-stretch">
              {isShare ? (
                <button
                  type="button"
                  onClick={() => share()}
                  className="flex flex-col items-center justify-center gap-1 py-2 px-2 w-full min-h-[44px]"
                  style={{ color: "var(--caileve-muted)" }}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                  <span className="text-[11px] tracking-[0.2em] uppercase truncate max-w-full">{label}</span>
                </button>
              ) : (
                <Link
                  to={to as string}
                  className="flex flex-col items-center justify-center gap-1 py-2 px-2 min-h-[44px] w-full"
                  style={{ color: active ? "var(--caileve-ink)" : "var(--caileve-muted)" }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                  <span
                    className="text-[11px] tracking-[0.2em] uppercase truncate max-w-full"
                    style={{ color: active ? "var(--caileve-lilac-text)" : "var(--caileve-muted)" }}
                  >
                    {label}
                  </span>
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

